import { useRef } from "react";
import { Button, FormRow } from "@omnabase/ui";
import { LOGO_MAX_BYTES, LOGO_TYPES } from "@omnabase/shared";
import { api, ApiError, wrote } from "../../lib/api";
import type { ToastOptions } from "../../shell/toast";

/** The Logo row (2026-10-20): the workspace's under Settings › General › Appearance, a project's under its Identity beside Mark; both for
 *  workspace administrators. A PNG, JPEG or SVG up to 2 MB, uploaded like a profile photo; the preview is the rail's mark square, so what
 *  you see here is what the rail and the lists show. `url` is the logo's route (`…/logo`), `logo` the current public path or null. */
type Props = { url: string; logo: string | null; name: string; help: string; done: () => Promise<void>; toast: (m: string, o?: ToastOptions) => void };

export function LogoRow({ url, logo, name, help, done, toast }: Props) {
  const input = useRef<HTMLInputElement>(null);
  const take = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > LOGO_MAX_BYTES) { toast("The logo may be 2 MB at most", { kind: "error" }); return; }
    const body = new FormData(); body.append("file", file);
    const r = await fetch(url, { method: "PUT", body, credentials: "same-origin" }); wrote(url);
    if (!r.ok) { const j = await r.json().catch(() => ({})); toast((j as { error?: { message?: string } }).error?.message ?? "Could not upload the logo", { kind: "error" }); return; }
    await done(); toast("Logo updated", { kind: "success" });
  };
  const remove = async () => { try { await api.del(url); await done(); toast("Logo removed", { kind: "success" }); } catch (e) { toast(e instanceof ApiError ? e.message : "Could not remove the logo", { kind: "error" }); } };
  return (
    <FormRow label="Logo" audience="admins" help={help}>
      <span className="ob-logo-field">
        {logo && <span className="ob-logo-field__mark"><img className="ob-logo-field__img" src={logo} alt={`${name} logo`} /></span>}
        <input ref={input} type="file" accept={LOGO_TYPES.join(",")} hidden aria-label="Logo" onChange={(e) => { void take(e.target.files?.[0]); e.target.value = ""; }} />
        <Button size="sm" icon="upload" onClick={() => input.current?.click()}>{logo ? "Replace" : "Upload logo"}</Button>
        {logo && <Button size="sm" variant="ghost" onClick={() => void remove()}>Remove</Button>}
      </span>
    </FormRow>
  );
}
